import { useEffect, useRef, useState } from "react";
import { InfantReviewChip, isInfantReviewDue } from "@/components/InfantReviewChip";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, CheckCircle2, FileSignature, Loader2, Trash2, Printer, AlertTriangle, FileDown, Pencil, Stethoscope, X as XIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import PrintableForm, { FormRow } from "@/components/PrintableForm";
import { fmtDate, fmtDateTime } from "@/lib/dates";
import { formatPhone } from "@/lib/phone";
import AddChildEnrollmentForm from "@/components/AddChildEnrollmentForm";
import SignaturePad from "@/components/SignaturePad";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FORMS_META = [
  { code: "jfs_01234", title: "Child Enrollment & Health Information", subtitle: "Required per OAC 5101:2-12-15", chip: "DCY 01234" },
  { code: "jfs_01236", title: "Medical / Physical Care Plan", subtitle: "For children with ongoing medical needs", chip: "DCY 01236" },
  { code: "jfs_01217", title: "Request for Administration of Medication", subtitle: "Per OAC 5101:2-12-12", chip: "DCY 01217" },
  { code: "dcy_01299", title: "Incident / Injury Report", subtitle: "File within 24 hours of incident", chip: "DCY 01299" },
  { code: "dcy_01218", title: "Basic Infant Information", subtitle: "For children under 18 months", chip: "DCY 01218" },
  { code: "dcy_01305", title: "Child Medical Statement", subtitle: "Annual medical exam summary", chip: "DCY 01305" },
  { code: "dcy_01229", title: "Transition Letter", subtitle: "Auto-filed when a child ages up into a new classroom", chip: "DCY 01229" },
];

async function openOfficialPdf(child, code) {
  try {
    const res = await api.get(`/children/${child.id}/forms/${code}/pdf`, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    console.error(err);
    toast.error("Could not open the official Ohio PDF.");
  }
}

async function openWeeklyAttendance(child) {
  try {
    const today = new Date();
    const weekOf = today.toISOString().slice(0, 10);
    const res = await api.get(`/children/${child.id}/attendance/weekly/pdf`, {
      params: { week_of: weekOf },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    toast.error(formatApiError(err));
  }
}

export default function ChildDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [child, setChild] = useState(null);
  const [forms, setForms] = useState({}); // form_code -> latest record
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(null); // form_code
  const [editing, setEditing] = useState(false);
  const [addingSibling, setAddingSibling] = useState(false);  // iter-51
  const [incidents, setIncidents] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const c = await api.get(`/children/${id}`).then((r) => r.data);
      setChild(c);
      const all = await api.get(`/children/${id}/forms`).then((r) => r.data);
      const latest = {};
      for (const f of all) if (!latest[f.form_code]) latest[f.form_code] = f;
      setForms(latest);
      // Dedupe incidents by id (a freshly-filed DCY 01299 can briefly appear
      // twice while the list refetches — keep the most-recently-signed copy).
      const incidentsById = new Map();
      for (const f of all) {
        if (f.form_code !== "dcy_01299") continue;
        const prev = incidentsById.get(f.id);
        if (!prev || (f.signed_at || "") > (prev.signed_at || "")) {
          incidentsById.set(f.id, f);
        }
      }
      setIncidents(Array.from(incidentsById.values()).sort(
        (a, b) => (b.signed_at || "").localeCompare(a.signed_at || "")
      ));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const removeChild = async () => {
    if (!window.confirm(`Remove ${child.first_name} ${child.last_name}?`)) return;
    try { await api.delete(`/children/${id}`); toast.success("Removed"); nav("/dashboard/children"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !child) return <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>;

  return (
    <div data-testid="child-detail-page" className="space-y-8">
      <Link to="/dashboard/children" className="text-sm text-stone-500 hover:text-stone-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> All children
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Child record</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">{child.first_name} {child.last_name}</h1>
          <p className="mt-2 text-stone-500">Born {fmtDate(child.date_of_birth)}{child.room ? ` · ${child.room}` : ""} · Parent {child.parent_name} ({child.parent_email})</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="child-edit-btn" variant="outline" onClick={() => setEditing(true)} className="rounded-xl">
            <Pencil className="w-4 h-4 mr-1" /> Edit details
          </Button>
          <Button data-testid="child-add-sibling-btn" variant="outline" onClick={() => setAddingSibling(true)} className="rounded-xl border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <Plus className="w-4 h-4 mr-1" /> Add sibling
          </Button>
          <Button data-testid="child-weekly-attendance-btn" variant="outline" onClick={() => openWeeklyAttendance(child)} className="rounded-xl">
            <FileDown className="w-4 h-4 mr-1" /> DCY 01208 · This week
          </Button>
          <Button variant="ghost" data-testid="child-delete-btn" onClick={removeChild} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-1" /> Remove
          </Button>
        </div>
      </header>

      {/* Edit dialog — reuses the enrollment wizard in edit mode */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit {child.first_name} {child.last_name}</DialogTitle>
            <DialogDescription>Update details and (optionally) re-file the DCY 01234 with fresh signature.</DialogDescription>
          </DialogHeader>
          <AddChildEnrollmentForm
            editingChild={child}
            initialEnrollment={forms.jfs_01234 || null}
            onCreated={() => { setEditing(false); load(); }}
          />
        </DialogContent>
      </Dialog>

      {/* iter-51 — Add sibling: pre-fill every parent / address / emergency
          / medical / signature field from this child so the director only
          needs to type the new child's first name, last name + DOB. */}
      <Dialog open={addingSibling} onOpenChange={setAddingSibling}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add sibling of {child.first_name} {child.last_name}</DialogTitle>
            <DialogDescription>Parent info, address, emergency contact and signature are pre-filled — just type the new child's first name, last name and date of birth.</DialogDescription>
          </DialogHeader>
          <AddChildEnrollmentForm
            initialChild={{
              first_name: "", last_name: child.last_name || "",
              date_of_birth: "", age_group: "Toddler",
              parent_name: child.parent_name, parent_email: child.parent_email, parent_phone: child.parent_phone,
            }}
            initialEnrollment={forms.jfs_01234 ? { ...forms.jfs_01234, signature: "", signed_by_email: forms.jfs_01234.signed_by_email || "", signed_at: null } : null}
            onCreated={() => { setAddingSibling(false); toast.success("Sibling added — open them from the children list to print their DCY 01234."); }}
          />
        </DialogContent>
      </Dialog>

      <MedicalExamCard child={child} onChange={load} />

      <MedicalUploadsCard child={child} />

      <IncidentHistoryPanel child={child} incidents={incidents} onChange={load} />

      <Tabs defaultValue="jfs_01234">
        <TabsList className="bg-stone-100 p-1 rounded-xl h-auto flex-wrap gap-1">
          {FORMS_META.map((f) => {
            const filed = !!forms[f.code];
            const isInfantForm = f.code === "dcy_01218";
            const isInfant = (child.age_group || "") === "infant";
            const requiredForInfant = isInfantForm && isInfant && !filed;
            return (
              <TabsTrigger key={f.code} value={f.code} data-testid={`form-tab-${f.code}`}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg px-4 py-2.5 font-medium flex items-center gap-2">
                {filed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> : <AlertTriangle className={`w-3.5 h-3.5 ${requiredForInfant ? "text-amber-600" : "text-orange-600"}`} />}
                {f.chip}
                {isInfantForm && filed && (
                  <InfantReviewChip child={child} lastUpdated={forms[f.code].form_date || forms[f.code].signed_at} testid="form-tab-review-due-dcy_01218" />
                )}
                {requiredForInfant && (
                  <span data-testid={`form-tab-required-${f.code}`} className="ml-1 inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300">
                    Required for infants
                  </span>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {FORMS_META.map((f) => (
          <TabsContent key={f.code} value={f.code} className="mt-8">
            <FormCard meta={f} child={child} existing={forms[f.code]}
              onPrint={() => setPrinting(f.code)}
              onSaved={load} />
          </TabsContent>
        ))}
      </Tabs>

      {/* Printable preview */}
      {printing && forms[printing] && (
        <PrintableForm
          open
          onClose={() => setPrinting(null)}
          formCode={FORMS_META.find((m) => m.code === printing).chip}
          title={FORMS_META.find((m) => m.code === printing).title}
          subtitle={FORMS_META.find((m) => m.code === printing).subtitle}
          childName={`${child.first_name} ${child.last_name}`}
          signedBy={forms[printing].signature || forms[printing].parent_signature || forms[printing].staff_signature}
          signedAt={forms[printing].signed_at}
        >
          <PrintableBody code={printing} data={forms[printing]} child={child} />
        </PrintableForm>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Medical exam expiry — quick-edit popover with status badge.
// Required for infants/toddlers/preschoolers per Ohio rule 5101:2-12-22.
// School-age children traditionally use the school physical; we still render
// the card so directors can track it if their program requires it.
// ---------------------------------------------------------------------------
function medicalExamStatus(expiresOn) {
  if (!expiresOn) return { key: "missing", label: "No exam on file", tone: "stone" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(`${expiresOn}T00:00:00`);
  if (Number.isNaN(exp.getTime())) return { key: "missing", label: "No exam on file", tone: "stone" };
  const diffDays = Math.round((exp - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { key: "expired", label: `Expired ${Math.abs(diffDays)}d ago`, tone: "red" };
  if (diffDays <= 60) return { key: "expiring_soon", label: `Expires in ${diffDays}d`, tone: "amber" };
  return { key: "current", label: `Current · ${diffDays}d left`, tone: "emerald" };
}

const TONE_STYLES = {
  emerald: { card: "border-emerald-200 bg-emerald-50/70", icon: "bg-emerald-700/10 text-emerald-700", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  amber:   { card: "border-amber-300 bg-amber-50/70",     icon: "bg-amber-500/10 text-amber-700",      pill: "bg-amber-100 text-amber-800 border-amber-300" },
  red:     { card: "border-red-300 bg-red-50/70",         icon: "bg-red-500/10 text-red-700",          pill: "bg-red-100 text-red-800 border-red-300" },
  stone:   { card: "border-stone-200 bg-stone-50/70",     icon: "bg-stone-200 text-stone-600",         pill: "bg-stone-100 text-stone-700 border-stone-300" },
};

function MedicalExamCard({ child, onChange }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(child.medical_exam_expires_on || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDate(child.medical_exam_expires_on || ""); }, [child.medical_exam_expires_on]);

  const status = medicalExamStatus(child.medical_exam_expires_on);
  const tone = TONE_STYLES[status.tone];

  const save = async (newValue) => {
    setSaving(true);
    try {
      await api.put(`/children/${child.id}/medical-exam`, { medical_exam_expires_on: newValue || null });
      toast.success(newValue ? "Medical exam date saved." : "Medical exam date cleared.");
      setOpen(false);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      data-testid="medical-exam-card"
      className={`rounded-3xl border ${tone.card} p-6 sm:p-7 flex flex-wrap items-center justify-between gap-4`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${tone.icon}`}>
          <Stethoscope className="w-5 h-5" />
        </div>
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Medical exam</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span
              data-testid="medical-exam-status-pill"
              data-status={status.key}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border ${tone.pill}`}
            >
              {status.key === "current" && <CheckCircle2 className="w-3.5 h-3.5" />}
              {status.key === "expiring_soon" && <AlertTriangle className="w-3.5 h-3.5" />}
              {status.key === "expired" && <AlertTriangle className="w-3.5 h-3.5" />}
              {status.label}
            </span>
            {child.medical_exam_expires_on && (
              <span data-testid="medical-exam-date-display" className="text-sm text-stone-700">
                Expires {fmtDate(child.medical_exam_expires_on)}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-stone-500">
            DCY 01305 annual exam tracking · Ohio rule 5101:2-12-22
          </p>
        </div>
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button data-testid="medical-exam-edit-btn" variant="outline" className="rounded-xl">
            <Pencil className="w-4 h-4 mr-1" /> {child.medical_exam_expires_on ? "Update" : "Set expiry"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80">
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500">
                Medical exam expires on
              </Label>
              <Input
                data-testid="medical-exam-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 h-11"
              />
              <p className="mt-2 text-xs text-stone-500">
                Annual exams are valid for 13 months. The pill above turns amber within 60 days, red after expiry.
              </p>
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              {child.medical_exam_expires_on ? (
                <Button
                  data-testid="medical-exam-clear-btn"
                  variant="ghost"
                  size="sm"
                  onClick={() => save("")}
                  disabled={saving}
                  className="text-red-600 hover:bg-red-50"
                >
                  <XIcon className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              ) : <span />}
              <Button
                data-testid="medical-exam-save-btn"
                size="sm"
                onClick={() => save(date)}
                disabled={saving || !date}
                className="rounded-xl bg-emerald-700 hover:bg-emerald-800"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </section>
  );
}

function FormCard({ meta, child, existing, onPrint, onSaved }) {
  return (
    <section className="rounded-3xl border border-stone-200 bg-white p-8">
      {meta.code === "dcy_01218" && existing && isInfantReviewDue(child, existing.form_date || existing.signed_at) && (
        <div data-testid="dcy01218-review-due-banner" className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <strong>Review due.</strong> This Basic Infant form was last updated {fmtDate(existing.form_date || existing.signed_at)}. Infants' feeding, sleep and care needs change quickly — please review the answers below with the parent and re-sign.
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl grid place-items-center ${existing ? "bg-emerald-700/10 text-emerald-700" : "bg-orange-500/10 text-orange-600"}`}>
            <FileSignature className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-medium text-stone-900">{meta.chip} — {meta.title}</h2>
            <p className="text-sm text-stone-500">{meta.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* DCY 01305 (medical statement) is filled by the child's doctor at
              an annual exam — directors typically print a blank form pre-stamped
              with the child's name + DOB and hand it to the parent to take to
              the appointment. This shortcut button skips the wizard entirely. */}
          {meta.code === "dcy_01305" && !existing && (
            <Button
              variant="outline"
              data-testid="print-blank-dcy_01305-btn"
              onClick={() => openOfficialPdf(child, meta.code)}
              className="rounded-xl bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
            >
              <Printer className="w-4 h-4 mr-1" /> Print blank for doctor (with child info)
            </Button>
          )}
          {existing && (
            <Button variant="outline" data-testid={`open-official-${meta.code}-btn`} onClick={() => openOfficialPdf(child, meta.code)} className="rounded-xl">
              <FileDown className="w-4 h-4 mr-1" /> Official Ohio PDF
            </Button>
          )}
          {existing && (
            <Button variant="outline" data-testid={`print-${meta.code}-btn`} onClick={onPrint} className="rounded-xl">
              <Printer className="w-4 h-4 mr-1" /> Quick print
            </Button>
          )}
          {existing && (
            <span data-testid={`signed-badge-${meta.code}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5" /> Filed · {fmtDate(existing.signed_at)}
            </span>
          )}
        </div>
      </div>

      <div className="mt-8">
        {meta.code === "jfs_01234" && <FormJFS01234 child={child} existing={existing} onSaved={onSaved} />}
        {meta.code === "jfs_01236" && <FormJFS01236 child={child} existing={existing} onSaved={onSaved} />}
        {meta.code === "jfs_01217" && <FormJFS01217 child={child} existing={existing} onSaved={onSaved} />}
        {meta.code === "dcy_01299" && <FormDCY01299 child={child} existing={existing} onSaved={onSaved} />}
        {meta.code === "dcy_01218" && <FormDCY01218 child={child} existing={existing} onSaved={onSaved} />}
        {meta.code === "dcy_01305" && <FormDCY01305 child={child} existing={existing} onSaved={onSaved} />}
      </div>
    </section>
  );
}

function useFormState(initial, existing) {
  const [form, setForm] = useState({ ...initial, ...(existing || {}) });
  const [saving, setSaving] = useState(false);
  return { form, setForm, saving, setSaving, up: (k, v) => setForm((f) => ({ ...f, [k]: v })) };
}

const Field = ({ label, value, onChange, testid, type = "text", required }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-stone-500">{label}{required && <span className="text-orange-500"> *</span>}</Label>
    <Input data-testid={testid} type={type} value={value || ""} required={required} onChange={(e) => onChange(e.target.value)} className="mt-2 h-11" />
  </div>
);

const Area = ({ label, value, onChange, testid, rows = 3 }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
    <Textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="mt-2" />
  </div>
);

async function saveForm(code, child, payload, setSaving, onSaved) {
  setSaving(true);
  try {
    await api.post(`/children/${child.id}/forms/${code}`, payload);
    toast.success(`${code.toUpperCase().replace("_", " ")} filed.`);
    if (typeof onSaved === "function") onSaved();
  } catch (err) { toast.error(formatApiError(err)); }
  finally { setSaving(false); }
}

function FormJFS01234({ child, existing, onSaved }) {
  // iter-45 — The full DCY 01234 wizard (Add/Edit child) collects every
  // field this legacy form was duplicating (home address, emergency contact,
  // parent #1 + #2 work info, etc.). Showing those inputs again on the
  // Forms tab confused directors who had already signed via the wizard. This
  // tab now shows a read-only summary plus a button to relaunch the wizard
  // for any updates. (Quick print / Official Ohio PDF buttons live on the
  // FormCard header above.)
  if (!existing) {
    return (
      <div data-testid="form-jfs_01234-pending" className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex items-start gap-3">
        <FileSignature className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-amber-900">Not yet filed</div>
          <p className="text-sm text-amber-800 mt-1">
            Open <b>Edit details</b> at the top of this page to complete the full DCY 01234 enrollment wizard. The wizard captures everything Ohio requires — home address, parent work info, emergency contacts, medical, signature — in one guided flow.
          </p>
        </div>
      </div>
    );
  }
  const e = existing || {};
  const phone = (v) => formatPhone(v) || "—";
  const Cell = ({ k, v }) => (
    <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">{k}</div>
      <div className="mt-0.5 text-sm text-stone-900 truncate">{v || "—"}</div>
    </div>
  );
  return (
    <div data-testid="form-jfs_01234" className="space-y-5">
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-emerald-900">
          <CheckCircle2 className="w-4 h-4" />
          <span>This enrollment was filed via the wizard. To update any field, click <b>Edit details</b> at the top.</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Cell k="Home address" v={[e.address, e.city, e.state, e.zip].filter(Boolean).join(", ")} />
        <Cell k="Home phone" v={phone(e.home_phone)} />
      </div>

      <div className="text-xs uppercase tracking-[0.18em] font-bold text-stone-600 mt-2">Parent / Guardian #1</div>
      <div className="grid sm:grid-cols-3 gap-2">
        <Cell k="Name" v={e.parent1_name} />
        <Cell k="Relationship" v={e.parent1_relationship} />
        <Cell k="Cell" v={phone(e.parent1_cell)} />
        <Cell k="Email" v={e.parent1_email} />
        <Cell k="Work / School" v={e.parent1_work_name} />
        <Cell k="Work address" v={[e.parent1_work_address, e.parent1_work_city, e.parent1_work_state].filter(Boolean).join(", ")} />
        <Cell k="Work phone" v={phone(e.parent1_work_phone)} />
      </div>

      {(!e.parent2_not_applicable && (e.parent2_name || e.parent2_work_name)) && (
        <>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-stone-600 mt-2">Parent / Guardian #2</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Cell k="Name" v={e.parent2_name} />
            <Cell k="Relationship" v={e.parent2_relationship} />
            <Cell k="Cell" v={phone(e.parent2_cell)} />
            <Cell k="Email" v={e.parent2_email} />
            <Cell k="Work / School" v={e.parent2_work_name} />
            <Cell k="Work address" v={[e.parent2_work_address, e.parent2_work_city, e.parent2_work_state].filter(Boolean).join(", ")} />
            <Cell k="Work phone" v={phone(e.parent2_work_phone)} />
          </div>
        </>
      )}
      {e.parent2_not_applicable && (
        <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-700">
          <b>Parent / Guardian #2:</b> single-parent household — printed form shows a strike-through.
        </div>
      )}

      <div className="text-xs uppercase tracking-[0.18em] font-bold text-stone-600 mt-2">Emergency contacts</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Cell k="Contact #1" v={e.emergency_contact_name} />
        <Cell k="Phone" v={phone(e.emergency_contact_phone)} />
        <Cell k="Relation" v={e.emergency_contact_relation} />
        <Cell k="City / State" v={[e.emergency_contact_city, e.emergency_contact_state].filter(Boolean).join(", ")} />
        {e.emergency2_contact_name && <>
          <Cell k="Contact #2" v={e.emergency2_contact_name} />
          <Cell k="Phone" v={phone(e.emergency2_contact_phone)} />
          <Cell k="Relation" v={e.emergency2_contact_relation} />
          <Cell k="City / State" v={[e.emergency2_contact_city, e.emergency2_contact_state].filter(Boolean).join(", ")} />
        </>}
      </div>

      <div className="text-xs uppercase tracking-[0.18em] font-bold text-stone-600 mt-2">Medical</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <Cell k="Allergies" v={e.allergies} />
        <Cell k="Medications" v={e.medications} />
        <Cell k="Conditions" v={e.medical_conditions} />
      </div>

      <div className="text-xs uppercase tracking-[0.18em] font-bold text-stone-600 mt-2">Signature</div>
      <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium text-stone-900">{(e.signature || "").startsWith("data:") ? "Drawn on screen" : (e.signature || "—")}</div>
          <div className="text-xs text-stone-500">{e.signed_by_email || ""} · {fmtDate(e.signed_at)}</div>
        </div>
      </div>
    </div>
  );
}

function FormJFS01236({ child, existing, onSaved }) {
  const init = { diagnosis: "", symptoms_description: "", triggers: "", medications_required: "", treatment_plan: "", emergency_action: "", physician_name: "", physician_phone: "", physician_signature_date: "", parent_signature: child.parent_name || "", signed_by_email: child.parent_email };
  const { form, up, saving, setSaving } = useFormState(init, existing);
  const submit = (e) => { e.preventDefault(); if (!form.parent_signature.trim()) return toast.error("Type signature."); saveForm("jfs_01236", child, form, setSaving, onSaved); };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="form-jfs_01236">
      <div className="sm:col-span-2"><Field label="Diagnosis" testid="jfs01236-diagnosis" value={form.diagnosis} onChange={(v) => up("diagnosis", v)} required /></div>
      <div className="sm:col-span-2"><Area label="Symptoms description" testid="jfs01236-symptoms" value={form.symptoms_description} onChange={(v) => up("symptoms_description", v)} /></div>
      <Area label="Triggers" testid="jfs01236-triggers" value={form.triggers} onChange={(v) => up("triggers", v)} />
      <Area label="Medications required" testid="jfs01236-meds" value={form.medications_required} onChange={(v) => up("medications_required", v)} />
      <div className="sm:col-span-2"><Area label="Treatment plan" testid="jfs01236-treatment" value={form.treatment_plan} onChange={(v) => up("treatment_plan", v)} rows={4} /></div>
      <div className="sm:col-span-2"><Area label="Emergency action" testid="jfs01236-emergency" value={form.emergency_action} onChange={(v) => up("emergency_action", v)} rows={3} /></div>
      <Field label="Physician name" testid="jfs01236-phys-name" value={form.physician_name} onChange={(v) => up("physician_name", v)} required />
      <Field label="Physician phone" testid="jfs01236-phys-phone" value={form.physician_phone} onChange={(v) => up("physician_phone", v)} required />
      <Field label="Physician signature date" testid="jfs01236-phys-date" type="date" value={form.physician_signature_date} onChange={(v) => up("physician_signature_date", v)} required />
      <SignatureBlock form={form} up={up} sigField="parent_signature" emailField="signed_by_email" code="jfs01236" />
      <SubmitRow saving={saving} existing={existing} testid="jfs01236-submit" />
    </form>
  );
}

function FormJFS01217({ child, existing, onSaved }) {
  const init = { medication_name: "", dosage: "", route: "oral", times_per_day: "", start_date: "", end_date: "", reason: "", physician_name: "", physician_phone: "", parent_signature: child.parent_name || "", signed_by_email: child.parent_email };
  const { form, up, saving, setSaving } = useFormState(init, existing);
  const submit = (e) => { e.preventDefault(); if (!form.parent_signature.trim()) return toast.error("Type signature."); saveForm("jfs_01217", child, form, setSaving, onSaved); };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="form-jfs_01217">
      <Field label="Medication name" testid="jfs01217-med-name" value={form.medication_name} onChange={(v) => up("medication_name", v)} required />
      <Field label="Dosage" testid="jfs01217-dosage" value={form.dosage} onChange={(v) => up("dosage", v)} required />
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Route</Label>
        <Select value={form.route} onValueChange={(v) => up("route", v)}>
          <SelectTrigger data-testid="jfs01217-route" className="mt-2 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="oral">Oral</SelectItem><SelectItem value="topical">Topical</SelectItem>
            <SelectItem value="inhaled">Inhaled</SelectItem><SelectItem value="injection">Injection</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Field label="Times per day" testid="jfs01217-times" value={form.times_per_day} onChange={(v) => up("times_per_day", v)} required />
      <Field label="Start date" testid="jfs01217-start" type="date" value={form.start_date} onChange={(v) => up("start_date", v)} required />
      <Field label="End date (optional)" testid="jfs01217-end" type="date" value={form.end_date} onChange={(v) => up("end_date", v)} />
      <div className="sm:col-span-2"><Area label="Reason for medication" testid="jfs01217-reason" value={form.reason} onChange={(v) => up("reason", v)} /></div>
      <Field label="Physician name" testid="jfs01217-phys-name" value={form.physician_name} onChange={(v) => up("physician_name", v)} required />
      <Field label="Physician phone" testid="jfs01217-phys-phone" value={form.physician_phone} onChange={(v) => up("physician_phone", v)} required />
      <SignatureBlock form={form} up={up} sigField="parent_signature" emailField="signed_by_email" code="jfs01217" />
      <SubmitRow saving={saving} existing={existing} testid="jfs01217-submit" />
    </form>
  );
}

// DCY 01299 checkbox groups — exact button names from the state PDF.
// Selecting any of these tick the matching checkbox on the generated form.
const DCY01299_BODY_PARTS = [
  "Head", "Eye", "Ear", "Nose", "MouthTeeth", "Face", "Chin", "Neck",
  "ShoulderCollarbone", "Arm", "Hand", "Fingers",
  "Front of TrunkStomach", "Back", "GenitalsButtocks",
  "Leg", "Knee", "Foot", "Toe", "Stubbed FingerToe",
  "LungsDifficulty Breathing", "Throat", "Whole Body",
];
const DCY01299_INJURY_TYPES = [
  "BumpBruise", "Cut", "ScrapeScratch", "Burn", "Puncture Wound",
  "Bit tongueCheekLip", "BiteHuman", "BiteStingAnimal or Insect",
  "SwellingRedness", "Nosebleed", "Sunburn", "Diaper Rash",
  "Object Inserted into Body", "Something in Eye", "Choking",
  "Fever", "StomachacheVomitingDiarrhea", "Difficulty Breathing",
  "Blood or Bruise Found on Child", "Other Illness specify in summary section",
];
const DCY01299_FIRST_AID = [
  "Ice", "Bandage", "Pressure Applied", "WashedSoaped",
  "Body Part Elevated", "HugPat", "Rested on Cot",
  "Referred for Further Medical Care", "Returned to Normal Activity",
];
const DCY01299_LOCATIONS = [
  "Classroom", "Inside Play AreaLarge Muscle Area", "Outdoor Play Area",
  "KitchenEating Area", "Bathroom", "Changing Table", "Crib",
  "High Chair", "HallDoorway", "Stairway", "Parking AreaDriveway",
  "Pool", "In Vehicle",
];
const DCY01299_ACTIVITIES = [
  "Indoor PlayGroup ActivitiesFree Play", "Outdoor Play",
  "Classroom Activity", "NaptimeRest Period", "MealsSnacks",
  "Diaper Change", "Transition Between Activities", "ArrivalDeparture",
  "BusVehicleDuring Transportation", "On FieldtripRoutine Trip",
  "Fall WalkRunTrip", "Fall to Surface", "Collision wObject", "Collision wPerson",
  "Fighting", "Baby Fed Wrong Bottle",
];
const DCY01299_FOLLOWUP = [
  "Were parents contacted", "Sent Home EarlyPicked Up Early",
  "Contacted Childrens Protective Services", "Referred for Further Medical Care",
];

function CheckboxGrid({ label, options, selected, onToggle, testidBase }) {
  return (
    <div className="sm:col-span-2">
      <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const on = selected.includes(opt);
          const slug = opt.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "").toLowerCase();
          return (
            <button
              type="button"
              key={opt}
              data-testid={`${testidBase}-${slug}`}
              onClick={() => onToggle(opt)}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition ${
                on ? "bg-emerald-700 text-white border-emerald-700"
                   : "bg-white text-stone-600 border-stone-200 hover:border-emerald-300"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FormDCY01299({ child, existing, onSaved }) {
  const init = {
    incident_date: "", incident_time: "", location: "", description: "",
    action_taken: "", parents_notified: "Parents contacted by phone at time of incident",
    parent_notified_at: "", witnesses: "",
    // 4 new free-text fields (iter-78) — the backend PDF mapping has always
    // referenced these, but the entry form was missing them so every
    // printed DCY 01299 left them blank.
    completed_by: "", completed_by_phone: "", parent_received_by: "",
    // Check-box arrays — each entry must be the EXACT button name on the PDF.
    body_parts_affected: [], injury_types: [], first_aid_given: [],
    location_checkboxes: [], activity_checkboxes: [], followup_checkboxes: [],
    staff_signature: "", signed_by_email: "",
  };
  const { form, up, saving, setSaving } = useFormState(init, existing);
  const toggle = (field, value) => {
    const cur = Array.isArray(form[field]) ? form[field] : [];
    up(field, cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]);
  };
  const submit = (e) => {
    e.preventDefault();
    if (!form.staff_signature || (typeof form.staff_signature === "string" && !form.staff_signature.trim())) {
      return toast.error("Add a staff signature.");
    }
    saveForm("dcy_01299", child, form, setSaving, onSaved);
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="form-dcy_01299">
      <Field label="Incident date" testid="dcy01299-date" type="date" value={form.incident_date} onChange={(v) => up("incident_date", v)} required />
      <Field label="Incident time" testid="dcy01299-time" type="time" value={form.incident_time} onChange={(v) => up("incident_time", v)} required />
      <div className="sm:col-span-2"><Field label="Location (free-text)" testid="dcy01299-location" value={form.location} onChange={(v) => up("location", v)} placeholder="e.g., Preschool Room A during outdoor play" required /></div>
      <div className="sm:col-span-2"><Area label="Description of incident (flows onto the official summary lines)" testid="dcy01299-description" value={form.description} onChange={(v) => up("description", v)} rows={4} /></div>

      <CheckboxGrid label="Body parts affected" options={DCY01299_BODY_PARTS} selected={form.body_parts_affected || []} onToggle={(v) => toggle("body_parts_affected", v)} testidBase="dcy01299-body" />
      <CheckboxGrid label="Injury / illness type" options={DCY01299_INJURY_TYPES} selected={form.injury_types || []} onToggle={(v) => toggle("injury_types", v)} testidBase="dcy01299-injury" />
      <CheckboxGrid label="First aid given" options={DCY01299_FIRST_AID} selected={form.first_aid_given || []} onToggle={(v) => toggle("first_aid_given", v)} testidBase="dcy01299-firstaid" />
      <CheckboxGrid label="Location checkboxes" options={DCY01299_LOCATIONS} selected={form.location_checkboxes || []} onToggle={(v) => toggle("location_checkboxes", v)} testidBase="dcy01299-loc" />
      <CheckboxGrid label="Activity at time of incident" options={DCY01299_ACTIVITIES} selected={form.activity_checkboxes || []} onToggle={(v) => toggle("activity_checkboxes", v)} testidBase="dcy01299-activity" />
      <CheckboxGrid label="Follow-up" options={DCY01299_FOLLOWUP} selected={form.followup_checkboxes || []} onToggle={(v) => toggle("followup_checkboxes", v)} testidBase="dcy01299-followup" />

      <Field label="Witnesses (names)" testid="dcy01299-witnesses" value={form.witnesses} onChange={(v) => up("witnesses", v)} />
      <Field label="Parents notified at" testid="dcy01299-parent-time" type="datetime-local" value={form.parent_notified_at} onChange={(v) => up("parent_notified_at", v)} />
      <div className="sm:col-span-2"><Area label="Action taken (flows onto the official action lines)" testid="dcy01299-action" value={form.action_taken} onChange={(v) => up("action_taken", v)} rows={3} /></div>

      {/* iter-78 — 4 free-text fields the state form asks for that were
          previously missing from the entry UI. All optional; printed only
          when the director types something. */}
      <Field
        label="Person completing this form (name)"
        testid="dcy01299-completed-by"
        value={form.completed_by}
        onChange={(v) => up("completed_by", v)}
        placeholder="e.g., Jane Doe, Director"
      />
      <Field
        label="Phone of person completing form"
        testid="dcy01299-completed-by-phone"
        type="tel"
        value={form.completed_by_phone}
        onChange={(v) => up("completed_by_phone", v)}
        placeholder="(614) 555-0100"
      />
      <div className="sm:col-span-2">
        <Field
          label="Parent / family member who received this report"
          testid="dcy01299-parent-received-by"
          value={form.parent_received_by}
          onChange={(v) => up("parent_received_by", v)}
          placeholder="Defaults to child's parent on file if left blank"
        />
      </div>

      <SignatureBlock form={form} up={up} sigField="staff_signature" emailField="signed_by_email" code="dcy01299" sigLabel="Staff signature" />
      <SubmitRow saving={saving} existing={existing} testid="dcy01299-submit" />
    </form>
  );
}

const ChoiceRow = ({ label, hint, options, isOn, onPick, testidBase }) => (
  <div className="sm:col-span-2">
    <Label className="text-xs uppercase tracking-widest text-stone-500">{label}</Label>
    {hint && <p className="text-xs text-stone-500 mt-1">{hint}</p>}
    <div className="mt-2 flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o.id} type="button" data-testid={`${testidBase}-${o.id}`} onClick={() => onPick(o.id)}
          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${isOn(o.id) ? "border-emerald-700 bg-emerald-700 text-white" : "border-stone-300 bg-white text-stone-700 hover:border-emerald-400"}`}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

function FormDCY01218({ child, existing, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const init = { nickname: "", siblings: "", feeding_types: [], formula_brand: "", formula_prep: "", feeding_amount: "", feeding_frequency: "", bottle_temp: "", juice_notes: "", uses_cup: "", solid_foods: "", foods_temp: "", table_food: "", security_items: "", nap_schedule: "", sleep_hints: "", sleep_position: "back", special_precautions: "", notes: "", parent_signature: child.parent_name || "", signed_date: today, staff_signature: "", caregiver_date: "", form_date: today, signed_by_email: child.parent_email };
  const { form, up, saving, setSaving } = useFormState(init, existing ? { ...existing, form_date: today, signed_date: today } : null);
  const feeding = form.feeding_types || [];
  const toggleFeeding = (id) => up("feeding_types", feeding.includes(id) ? feeding.filter((x) => x !== id) : [...feeding, id]);
  const cup = String(form.uses_cup || "").trim().toLowerCase().startsWith("y") ? "yes" : String(form.uses_cup || "").trim().toLowerCase().startsWith("n") ? "no" : "";
  const submit = (e) => { e.preventDefault(); if (!form.parent_signature.trim()) return toast.error("Type signature."); saveForm("dcy_01218", child, form, setSaving, onSaved); };
  const SLEEP_OPTS = [
    { id: "back", label: "Back", recommended: true },
    { id: "side", label: "Side*" },
    { id: "tummy", label: "Tummy*" },
  ];
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="form-dcy_01218">
      <Field label="Nickname" testid="dcy01218-nickname" value={form.nickname} onChange={(v) => up("nickname", v)} />
      <Field label="Siblings" testid="dcy01218-siblings" value={form.siblings} onChange={(v) => up("siblings", v)} />
      <ChoiceRow label="What are you feeding your infant? (check all that apply)" testidBase="dcy01218-feeding"
        options={[{ id: "formula", label: "Formula" }, { id: "breast_milk", label: "Breast milk" }]}
        isOn={(id) => feeding.includes(id)} onPick={toggleFeeding} />
      {feeding.includes("formula") && <div className="sm:col-span-2"><Field label="Formula brand" testid="dcy01218-formula-brand" value={form.formula_brand} onChange={(v) => up("formula_brand", v)} /></div>}
      <div className="sm:col-span-2"><Area label="Formula preparation (if center/provider is to prepare)" testid="dcy01218-formula" value={form.formula_prep} onChange={(v) => up("formula_prep", v)} /></div>
      <Field label="Amount for each feeding" testid="dcy01218-feed-amount" value={form.feeding_amount} onChange={(v) => up("feeding_amount", v)} />
      <Field label="Frequency of feedings" testid="dcy01218-feed-freq" value={form.feeding_frequency} onChange={(v) => up("feeding_frequency", v)} />
      <ChoiceRow label="My infant likes a bottle warmed (check one)" testidBase="dcy01218-bottle"
        options={[{ id: "room_temp", label: "Room temp" }, { id: "warm", label: "Warm" }, { id: "very_warm", label: "Very warm / NOT HOT" }]}
        isOn={(id) => form.bottle_temp === id} onPick={(id) => up("bottle_temp", form.bottle_temp === id ? "" : id)} />
      <div className="sm:col-span-2"><Field label="Juice (type, amount, when?)" testid="dcy01218-juice" value={form.juice_notes} onChange={(v) => up("juice_notes", v)} /></div>
      <ChoiceRow label="Does child use a cup yet?" testidBase="dcy01218-cup"
        options={[{ id: "no", label: "No" }, { id: "yes", label: "Yes" }]}
        isOn={(id) => cup === id} onPick={(id) => up("uses_cup", cup === id ? "" : id)} />
      <div className="sm:col-span-2">
        <Area label="Solid foods (baby food, brand, types, amounts, frequency)" testid="dcy01218-solid-foods" value={form.solid_foods} onChange={(v) => up("solid_foods", v)} rows={2} />
        <p className="text-xs text-amber-800 mt-1.5" data-testid="dcy01218-solid-foods-note">* You must have written permission from your child's physician if your child is under 4 months and given solid foods.</p>
      </div>
      <div className="sm:col-span-2">
        <Field label="Are foods served room temperature or warmed?" testid="dcy01218-foods-temp" value={form.foods_temp} onChange={(v) => up("foods_temp", v)} />
        <div className="mt-2 flex gap-2">
          {["Room temperature", "Warmed"].map((t) => (
            <button key={t} type="button" data-testid={`dcy01218-foods-temp-${t.split(" ")[0].toLowerCase()}`} onClick={() => up("foods_temp", t)}
              className={`rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${form.foods_temp === t ? "border-emerald-700 bg-emerald-700 text-white" : "border-stone-200 bg-white text-stone-600 hover:border-emerald-300"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2"><Area label="Table food (types, amounts, frequency, special instructions)" testid="dcy01218-table-food" value={form.table_food} onChange={(v) => up("table_food", v)} /></div>
      <Field label="Security items (pacifier, blankies, etc.)" testid="dcy01218-security" value={form.security_items} onChange={(v) => up("security_items", v)} />
      <Field label="Nap schedule" testid="dcy01218-nap" value={form.nap_schedule} onChange={(v) => up("nap_schedule", v)} />
      <div className="sm:col-span-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3" data-testid="dcy01218-sleep-position-block">
        <div>
          <Label className="text-sm font-semibold text-stone-900">Sleeping position</Label>
          <p className="text-xs text-stone-600 mt-0.5">Default is <strong>Back</strong> per safe-sleep guidance.</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {SLEEP_OPTS.map((o) => {
            const active = form.sleep_position === o.id;
            return (
              <button
                key={o.id}
                type="button"
                data-testid={`dcy01218-sleep-${o.id}`}
                onClick={() => up("sleep_position", o.id)}
                className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition ${active ? "border-emerald-700 bg-emerald-700 text-white shadow-sm" : "border-stone-300 bg-white text-stone-700 hover:border-emerald-400"}`}
              >
                {o.label}
                {o.recommended && active ? <span className="ml-1 text-[10px] uppercase tracking-wider">· recommended</span> : null}
              </button>
            );
          })}
        </div>
        {(form.sleep_position === "side" || form.sleep_position === "tummy") && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="dcy01218-sleep-waiver">
            <strong>* Waiver required.</strong> You must secure a sleep-position waiver from your child's physician
            (DCY 01235) before staff can place your baby on their {form.sleep_position}. Please contact the center to file it.
          </div>
        )}
      </div>
      <div className="sm:col-span-2"><Area label="Hints for getting baby to sleep" testid="dcy01218-sleep-hints" value={form.sleep_hints} onChange={(v) => up("sleep_hints", v)} /></div>
      <div className="sm:col-span-2"><Area label="Special precautions" testid="dcy01218-precautions" value={form.special_precautions} onChange={(v) => up("special_precautions", v)} /></div>
      <div className="sm:col-span-2"><Area label="Anything else helpful for staff" testid="dcy01218-notes" value={form.notes} onChange={(v) => up("notes", v)} rows={4} /></div>
      <SignatureBlock form={form} up={up} sigField="parent_signature" emailField="signed_by_email" code="dcy01218" sigLabel="Parent signature" />
      <Field label="Parent signature date" testid="dcy01218-signed-date" type="date" value={form.signed_date} onChange={(v) => up("signed_date", v)} />
      <Field label="Date form last updated" testid="dcy01218-form-date" type="date" value={form.form_date} onChange={(v) => up("form_date", v)} />
      <div className="sm:col-span-2 rounded-xl border border-stone-200 bg-white p-5 grid sm:grid-cols-2 gap-4" data-testid="dcy01218-caregiver-block">
        <div className="sm:col-span-2">
          <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">Primary caregiver (staff) — optional</div>
          <p className="text-xs text-stone-500 mt-1">Staff can type their name here now or later. Leave blank to sign by hand on the printed form.</p>
        </div>
        <Field label="Primary caregiver signature" testid="dcy01218-caregiver-signature" value={form.staff_signature} onChange={(v) => up("staff_signature", v)} />
        <Field label="Caregiver signature date" testid="dcy01218-caregiver-date" type="date" value={form.caregiver_date} onChange={(v) => up("caregiver_date", v)} />
      </div>
      <SubmitRow saving={saving} existing={existing} testid="dcy01218-submit" />
    </form>
  );
}

function FormDCY01305({ child, existing, onSaved }) {
  const init = { height: "", weight: "", bmi: "", vision: "", hemoglobin: "", lead: "", dental: "", other_screenings: "", exam_date: "", practitioner_name: "", practitioner_phone: "", practitioner_address: "", immunization_notes: "", additional_info: "", recommendations: "", allergies: "", conditions: "", notes: "", parent_signature: child.parent_name || "", signed_by_email: child.parent_email };
  const { form, up, saving, setSaving } = useFormState(init, existing);
  const submit = (e) => { e.preventDefault(); if (!form.parent_signature.trim()) return toast.error("Type signature."); saveForm("dcy_01305", child, form, setSaving, onSaved); };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5" data-testid="form-dcy_01305">
      <div className="sm:col-span-2 grid grid-cols-3 gap-3">
        <Field label="Height" testid="dcy01305-height" value={form.height} onChange={(v) => up("height", v)} />
        <Field label="Weight" testid="dcy01305-weight" value={form.weight} onChange={(v) => up("weight", v)} />
        <Field label="BMI" testid="dcy01305-bmi" value={form.bmi} onChange={(v) => up("bmi", v)} />
      </div>
      <Field label="Vision screening" testid="dcy01305-vision" value={form.vision} onChange={(v) => up("vision", v)} />
      <Field label="Hemoglobin" testid="dcy01305-hemoglobin" value={form.hemoglobin} onChange={(v) => up("hemoglobin", v)} />
      <Field label="Lead screening" testid="dcy01305-lead" value={form.lead} onChange={(v) => up("lead", v)} />
      <Field label="Dental" testid="dcy01305-dental" value={form.dental} onChange={(v) => up("dental", v)} />
      <div className="sm:col-span-2"><Field label="Other screenings" testid="dcy01305-other" value={form.other_screenings} onChange={(v) => up("other_screenings", v)} /></div>
      <Field label="Date of exam" testid="dcy01305-exam-date" type="date" value={form.exam_date} onChange={(v) => up("exam_date", v)} required />
      <Field label="Practitioner name" testid="dcy01305-pract-name" value={form.practitioner_name} onChange={(v) => up("practitioner_name", v)} required />
      <Field label="Practitioner phone" testid="dcy01305-pract-phone" value={form.practitioner_phone} onChange={(v) => up("practitioner_phone", v)} />
      <Field label="Practitioner address (city, state, zip)" testid="dcy01305-pract-addr" value={form.practitioner_address} onChange={(v) => up("practitioner_address", v)} />
      <div className="sm:col-span-2"><Area label="Immunization notes (attach record)" testid="dcy01305-immun" value={form.immunization_notes} onChange={(v) => up("immunization_notes", v)} /></div>
      <div className="sm:col-span-2"><Area label="Allergies" testid="dcy01305-allergies" value={form.allergies} onChange={(v) => up("allergies", v)} /></div>
      <div className="sm:col-span-2"><Area label="Chronic conditions" testid="dcy01305-conditions" value={form.conditions} onChange={(v) => up("conditions", v)} /></div>
      <div className="sm:col-span-2"><Area label="Recommendations from practitioner" testid="dcy01305-recs" value={form.recommendations} onChange={(v) => up("recommendations", v)} /></div>
      <div className="sm:col-span-2"><Area label="Additional information" testid="dcy01305-info" value={form.additional_info} onChange={(v) => up("additional_info", v)} /></div>
      <SignatureBlock form={form} up={up} sigField="parent_signature" emailField="signed_by_email" code="dcy01305" sigLabel="Parent signature" />
      <SubmitRow saving={saving} existing={existing} testid="dcy01305-submit" />
    </form>
  );
}

const SignatureBlock = ({ form, up, sigField, emailField, code, sigLabel = "Signature" }) => (
  <div className="sm:col-span-2 rounded-xl border border-stone-200 bg-stone-50 p-5">
    <div className="text-[11px] uppercase tracking-widest font-bold text-emerald-700">E-signature</div>
    <p className="text-xs text-stone-500 mt-1">Draw or type your signature — either counts as a legal e-signature.</p>
    <div className="mt-3 grid sm:grid-cols-2 gap-3">
      <SignaturePad
        value={form[sigField]}
        onChange={(v) => up(sigField, v)}
        label={sigLabel}
        testid={`${code}-signature-pad`}
        required
      />
      <Field label="Signed-by email" testid={`${code}-email`} type="email" value={form[emailField]} onChange={(v) => up(emailField, v)} required />
    </div>
  </div>
);

const SubmitRow = ({ saving, existing, testid }) => (
  <div className="sm:col-span-2 flex items-center justify-end">
    <Button type="submit" data-testid={testid} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl px-6 h-11">
      {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <>{existing ? "Update & re-sign" : "E-sign & file"}</>}
    </Button>
  </div>
);

function PrintableBody({ code, data, child }) {
  if (code === "jfs_01234") return (
    <dl className="space-y-1">
      <FormRow label="Child" value={`${child.first_name} ${child.last_name} · DOB ${fmtDate(child.date_of_birth)}`} />
      <FormRow label="Address" value={`${data.address}, ${data.city}, ${data.state} ${data.zip}`} />
      <FormRow label="Parent" value={`${child.parent_name} · ${child.parent_email}`} />
      <FormRow label="Emergency contact" value={`${data.emergency_contact_name} · ${data.emergency_contact_phone} (${data.emergency_contact_relation})`} />
      <FormRow label="Allergies" value={data.allergies} />
      <FormRow label="Medications" value={data.medications} />
      <FormRow label="Transport authorized" value={data.transportation_authorized ? "Yes" : "No"} />
      <FormRow label="Parent roster consent" value={data.parent_roster_consent ? "Yes" : "No"} />
    </dl>
  );
  if (code === "jfs_01236") return (
    <dl className="space-y-1">
      <FormRow label="Child" value={`${child.first_name} ${child.last_name}`} />
      <FormRow label="Diagnosis" value={data.diagnosis} />
      <FormRow label="Symptoms" value={data.symptoms_description} />
      <FormRow label="Triggers" value={data.triggers} />
      <FormRow label="Medications" value={data.medications_required} />
      <FormRow label="Treatment plan" value={data.treatment_plan} />
      <FormRow label="Emergency action" value={data.emergency_action} />
      <FormRow label="Physician" value={`${data.physician_name} · ${data.physician_phone}`} />
      <FormRow label="Physician signed" value={data.physician_signature_date} />
    </dl>
  );
  if (code === "jfs_01217") return (
    <dl className="space-y-1">
      <FormRow label="Child" value={`${child.first_name} ${child.last_name}`} />
      <FormRow label="Medication" value={`${data.medication_name} · ${data.dosage} · ${data.route}`} />
      <FormRow label="Times per day" value={data.times_per_day} />
      <FormRow label="Period" value={`${data.start_date} → ${data.end_date || "ongoing"}`} />
      <FormRow label="Reason" value={data.reason} />
      <FormRow label="Physician" value={`${data.physician_name} · ${data.physician_phone}`} />
    </dl>
  );
  if (code === "dcy_01299") return (
    <dl className="space-y-1">
      <FormRow label="Child" value={`${child.first_name} ${child.last_name}`} />
      <FormRow label="When" value={`${fmtDate(data.incident_date)} · ${data.incident_time}`} />
      <FormRow label="Location" value={data.location} />
      <FormRow label="Description" value={data.description} />
      <FormRow label="Body part" value={data.body_part_affected} />
      <FormRow label="Action taken" value={data.action_taken} />
      <FormRow label="Parent notified" value={data.parent_notified ? `Yes · ${data.parent_notified_at || ""}` : "No"} />
    </dl>
  );
  return null;
}

/* ------------------------------------------------------------------ */
/*  Incident Reports panel — lists every DCY 01299 filed for this     */
/*  child, with Print + Delete actions. Uses the existing /forms       */
/*  backend (enrollment_forms collection with form_code=dcy_01299).    */
/* ------------------------------------------------------------------ */
function IncidentHistoryPanel({ child, incidents, onChange }) {
  const openPdf = async (formId) => {
    try {
      const res = await api.get(`/children/${child.id}/forms/id/${formId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (formId) => {
    if (!window.confirm("Delete this incident report? This cannot be undone.")) return;
    try { await api.delete(`/children/${child.id}/forms/id/${formId}`); toast.success("Deleted"); onChange?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section data-testid="incident-history-panel" className="rounded-3xl border border-stone-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Reports · DCY 01299</div>
          <h2 className="mt-1 font-display text-xl font-medium text-stone-900">Incident / Injury history</h2>
          <p className="text-sm text-stone-500">Every filed report stays on record here — print the official DCY 01299 for any entry.</p>
        </div>
        <Button
          data-testid="incident-new-btn"
          onClick={() => {
            const tab = document.querySelector('[data-testid="form-tab-dcy_01299"]');
            if (tab) tab.click();
            setTimeout(() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" }), 100);
          }}
          className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl shrink-0"
        >
          <FileSignature className="w-4 h-4 mr-1" /> File new incident
        </Button>
      </div>

      {incidents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-8 text-center">
          <AlertTriangle className="w-6 h-6 mx-auto text-stone-400" />
          <p className="mt-2 text-sm text-stone-600">No incidents on file. Use the DCY 01299 tab below to file one.</p>
        </div>
      ) : (
        <ul className="divide-y divide-stone-100 border border-stone-200 rounded-xl overflow-hidden" data-testid="incident-list">
          {incidents.map((it) => (
            <li
              key={it.id}
              data-testid={`incident-row-${it.id}`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50"
            >
              <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 grid place-items-center shrink-0">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 truncate">
                  {it.incident_description || it.description || it.nature_of_injury || "Incident"}
                </div>
                <div className="text-[11px] text-stone-500 flex flex-wrap gap-2">
                  <span>Filed {fmtDateTime(it.signed_at)}</span>
                  {(it.incident_date || it.date_of_incident) && <span>· Event {fmtDate(it.incident_date || it.date_of_incident)}</span>}
                  {(it.incident_time || it.time_of_incident) && <span>at {it.incident_time || it.time_of_incident}</span>}
                  {(it.location || it.location_of_incident) && <span>· {it.location || it.location_of_incident}</span>}
                </div>
              </div>
              <Button
                data-testid={`incident-print-${it.id}`}
                variant="outline" size="sm"
                onClick={() => openPdf(it.id)}
                className="rounded-xl"
              >
                <FileDown className="w-3.5 h-3.5 mr-1" /> Print DCY 01299
              </Button>
              <Button
                data-testid={`incident-delete-${it.id}`}
                variant="ghost" size="sm"
                onClick={() => remove(it.id)}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


// ============ Medical Forms & Shot Records (Infants / Toddlers / Preschool only) ============
function MedicalUploadsCard({ child }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [label, setLabel] = useState("");
  const fileRef = useRef(null);

  // Gate the entire section on age group — hidden for school-age children.
  const ag = (child.age_group || "").toLowerCase();
  const norm = ag.startsWith("school") ? "school_age"
    : ag.startsWith("preschool") ? "preschool"
    : ag.startsWith("toddler") ? "toddler"
    : ag.startsWith("infant") ? "infant"
    : ag;
  const allowed = ["infant", "toddler", "preschool"].includes(norm);

  const load = async () => {
    try {
      const r = await api.get(`/children/${child.id}/medical-uploads`);
      setRows(r.data);
    } catch (err) { /* not allowed → keep empty */ }
  };
  useEffect(() => { if (allowed) load(); /* eslint-disable-next-line */ }, [child.id]);

  if (!allowed) return null;

  const upload = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Pick a file first"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("label", label || "");
    setBusy(true);
    try {
      await api.post(`/children/${child.id}/medical-uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded.");
      setLabel("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (err) {
      // formatApiError on a Blob 4xx returns generic — show specific status info instead.
      const msg = err?.response?.status === 413 ? "File too large (max 8 MB)"
        : err?.response?.status === 415 ? "Only PDF, JPG, or PNG allowed"
        : err?.response?.data?.detail || "Upload failed";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.file_name}"?`)) return;
    try { await api.delete(`/children/${child.id}/medical-uploads/${att.id}`); toast.success("Deleted."); await load(); }
    catch (err) { toast.error("Delete failed"); }
  };

  // JWT-aware fetch: download via Axios so the Authorization header is
  // attached (bare <a href> opens the URL without auth → 401).
  const fetchBlobUrl = async (att) => {
    const r = await api.get(`/children/${child.id}/medical-uploads/${att.id}`, { responseType: "blob" });
    const blob = new Blob([r.data], { type: att.content_type || r.data.type || "application/octet-stream" });
    return URL.createObjectURL(blob);
  };

  const view = async (att) => {
    try {
      const url = await fetchBlobUrl(att);
      const w = window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after a delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!w) toast.error("Pop-up blocked. Allow pop-ups to view files.");
    } catch (err) {
      toast.error("Failed to open file");
    }
  };

  const printFile = async (att) => {
    try {
      const url = await fetchBlobUrl(att);
      // Hidden iframe → trigger native print without leaving the page
      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.src = url;
      frame.onload = () => {
        try {
          frame.contentWindow?.focus();
          frame.contentWindow?.print();
        } catch {
          toast.error("Print failed");
        }
        // Cleanup after print dialog closes
        setTimeout(() => {
          URL.revokeObjectURL(url);
          frame.remove();
        }, 60_000);
      };
      document.body.appendChild(frame);
    } catch (err) {
      toast.error("Failed to print file");
    }
  };

  return (
    <section data-testid="medical-uploads-card" className="rounded-3xl border border-stone-200 bg-white p-8 space-y-4">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Records</div>
        <h2 className="mt-2 font-display text-2xl text-stone-900">Medical Forms & Shot Records</h2>
        <p className="mt-1 text-sm text-stone-500">Upload PDF, JPG, or PNG. Max 8 MB per file. Visible only for Infant, Toddler, and Preschool children.</p>
      </header>

      <form onSubmit={upload} data-testid="medical-upload-form" className="grid sm:grid-cols-[1fr_220px_auto] gap-2 items-end p-4 rounded-2xl bg-stone-50 border border-stone-200">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">File</Label>
          <Input
            ref={fileRef}
            data-testid="medical-upload-file"
            type="file"
            accept="application/pdf,image/png,image/jpeg,image/jpg"
            className="mt-1.5 h-10 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-emerald-700 file:text-white file:text-xs"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Label (optional)</Label>
          <Input
            data-testid="medical-upload-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Immunizations 2026"
            className="mt-1.5 h-10"
          />
        </div>
        <Button type="submit" data-testid="medical-upload-submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-10">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
        </Button>
      </form>

      {rows.length === 0 ? (
        <p className="text-sm text-stone-500">No medical files yet.</p>
      ) : (
        <ul data-testid="medical-uploads-list" className="space-y-2">
          {rows.map((r) => (
            <li key={r.id} data-testid={`medical-upload-${r.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-stone-200 bg-white">
              <div className="text-stone-400">{r.content_type === "application/pdf" ? "📄" : "🖼️"}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 text-sm truncate">{r.file_name}</div>
                <div className="text-[11px] text-stone-500">
                  {r.label && <span className="font-semibold text-emerald-700">{r.label}</span>}
                  {r.label && " · "}
                  {Math.round((r.size || 0) / 1024)} KB · uploaded {r.uploaded_by_name || "—"} · {fmtDate ? fmtDate(r.created_at) : r.created_at}
                </div>
              </div>
              <button
                type="button"
                data-testid={`medical-upload-view-${r.id}`}
                onClick={() => view(r)}
                className="text-xs font-medium text-emerald-700 hover:text-emerald-900 px-2 py-1 rounded-md border border-stone-200 hover:bg-stone-50"
              >
                View
              </button>
              <button
                type="button"
                data-testid={`medical-upload-print-${r.id}`}
                onClick={() => printFile(r)}
                className="text-xs font-medium text-stone-700 hover:text-stone-900 px-2 py-1 rounded-md border border-stone-200 hover:bg-stone-50"
              >
                Print
              </button>
              <button type="button" data-testid={`medical-upload-delete-${r.id}`} onClick={() => remove(r)} className="text-xs font-medium text-rose-600 hover:text-rose-800 px-2 py-1 rounded-md border border-stone-200 hover:bg-rose-50">
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
